// @systeme: interface
// Les retours des utilisatrices : « un souci, une idée ? ». C'est leur message à
// Juliette ; on le lit, on note ce qu'on en fait, on le marque traité.
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { RetourAdmin } from '@foyer/shared';
import { Chargement } from '../../composants/Chargement';
import { useMessages } from '../../composants/Messages';
import { api } from '../../lib/api';
import { formaterJourCourt, jourDe } from '../../lib/dates';

const TYPES: Record<RetourAdmin['type'], string> = {
  insatisfaction: 'Ça ne va pas',
  suggestion: 'Une idée',
  bug: 'Un bug',
  contenu_incorrect: 'C’est faux',
};

function CarteRetour({ retour, onMaj }: { retour: RetourAdmin; onMaj: (r: RetourAdmin) => void }) {
  const { dire, direErreur } = useMessages();
  const [reponse, setReponse] = useState(retour.reponse ?? '');

  async function traiter() {
    try {
      onMaj(await api.admin.traiterRetour(retour.id, reponse.trim() || undefined));
      dire('Marqué comme traité.');
    } catch (e) {
      direErreur(e);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        {TYPES[retour.type]} · {formaterJourCourt(jourDe(retour.creeLe))}
        {retour.ecran ? ` · écran ${retour.ecran}` : ''}
        {retour.traiteLe ? ' · traité' : ''}
      </Typography>
      <Typography sx={{ my: 0.5, whiteSpace: 'pre-wrap' }}>{retour.message}</Typography>
      {retour.traiteLe ? (
        retour.reponse && (
          <Typography variant="body2" color="text.secondary">
            Ce qu’on en a fait : {retour.reponse}
          </Typography>
        )
      ) : (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Ce qu’on en fait (facultatif)"
            value={reponse}
            onChange={(e) => setReponse(e.target.value)}
          />
          <Button variant="outlined" onClick={() => void traiter()} sx={{ whiteSpace: 'nowrap' }}>
            Traité
          </Button>
        </Stack>
      )}
    </Paper>
  );
}

export function Retours() {
  const { direErreur } = useMessages();
  const [retours, setRetours] = useState<RetourAdmin[] | undefined>();
  const [tous, setTous] = useState(false);

  const charger = useCallback(() => {
    api.admin.retours(tous).then(setRetours).catch(direErreur);
  }, [tous, direErreur]);
  useEffect(charger, [charger]);

  if (!retours) return <Chargement />;
  return (
    <Stack spacing={1.5}>
      <FormControlLabel
        control={<Switch checked={tous} onChange={(e) => setTous(e.target.checked)} />}
        label="Voir aussi les retours déjà traités"
      />
      {retours.length === 0 && <Typography color="text.secondary">Rien à traiter.</Typography>}
      {retours.map((r) => (
        <CarteRetour
          key={r.id}
          retour={r}
          onMaj={(maj) => setRetours((liste) => liste?.map((x) => (x.id === maj.id ? maj : x)))}
        />
      ))}
    </Stack>
  );
}
